import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, MapPin, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '' });
  const [breakdownLoc, setBreakdownLoc] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API}/locations`, { credentials: 'include' });
      const data = await res.json();
      setLocations(data);
    } catch (error) {
      toast.error('Erro ao carregar locais');
    } finally {
      setLoading(false);
    }
  };

  const openBreakdown = async (loc) => {
    setBreakdownLoc(loc);
    setBreakdown(null);
    setBreakdownLoading(true);
    try {
      const res = await axios.get(`${API}/locations/${loc.location_id}/breakdown`, { withCredentials: true });
      setBreakdown(res.data);
    } catch (err) {
      toast.error('Erro ao carregar funcionários do local');
    } finally {
      setBreakdownLoading(false);
    }
  };

  const DEFAULT_WEEKLY = { monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0 };

  const openNewDialog = () => {
    setEditingLocation(null);
    setFormData({ name: '', address: '', expected_daily_hours: 8, lunch_deduction: 1, weekly_hours: { ...DEFAULT_WEEKLY }, latitude: null, longitude: null, geo_radius: 100 });
    setDialogOpen(true);
  };

  const openEditDialog = (loc) => {
    setEditingLocation(loc);
    setFormData({ 
      name: loc.name, 
      address: loc.address || '',
      expected_daily_hours: loc.expected_daily_hours ?? 8,
      lunch_deduction: loc.lunch_deduction ?? 1,
      weekly_hours: loc.weekly_hours ? { ...DEFAULT_WEEKLY, ...loc.weekly_hours } : { ...DEFAULT_WEEKLY },
      latitude: loc.latitude || null,
      longitude: loc.longitude || null,
      geo_radius: loc.geo_radius || 100,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const url = editingLocation 
        ? `${API}/locations/${editingLocation.location_id}`
        : `${API}/locations`;
      
      const res = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Erro ao salvar');
      
      toast.success(editingLocation ? 'Local atualizado' : 'Local criado');
      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error('Erro ao salvar local');
    }
  };

  const getKioskUrl = (locationId) => {
    return `${window.location.origin}/kiosk/${locationId}`;
  };

  const copyKioskUrl = (locationId) => {
    navigator.clipboard.writeText(getKioskUrl(locationId));
    toast.success('Link do quiosque copiado');
  };

  if (loading) {
    return (
      <AdminLayout title="Locais">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Locais">
      <div className="mb-6">
        <Button onClick={openNewDialog} data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Local
        </Button>
      </div>

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {locations.length === 0 ? (
          <div className="col-span-full text-center py-16 border border-[#E5E7EB]">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-[#9CA3AF]" />
            <p className="text-[#6B7280]">Nenhum local cadastrado</p>
          </div>
        ) : (
          locations.map(loc => (
            <div 
              key={loc.location_id}
              className="border border-[#E5E7EB] p-6 hover:border-[#111111] transition-colors"
              data-testid={`location-card-${loc.location_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#111111] flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{loc.name}</h3>
                    <p className="text-sm text-[#6B7280]">{loc.address || 'Sem endereço'}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => openEditDialog(loc)}
                  data-testid={`edit-location-${loc.location_id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full mb-3 justify-center"
                onClick={() => openBreakdown(loc)}
                data-testid={`view-team-${loc.location_id}`}
              >
                <Users className="w-4 h-4 mr-2" />
                Ver Equipa por Setor
              </Button>

              <div className="pt-4 border-t border-[#E5E7EB]">
                <Label className="text-xs uppercase tracking-widest text-[#6B7280] mb-2 block">
                  Link do Quiosque
                </Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={getKioskUrl(loc.location_id)} 
                    readOnly 
                    className="text-xs mono"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyKioskUrl(loc.location_id)}
                    title="Copiar link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <Button 
                  variant="link" 
                  className="mt-2 p-0 h-auto text-xs"
                  onClick={() => window.open(getKioskUrl(loc.location_id), '_blank')}
                  data-testid={`open-kiosk-${loc.location_id}`}
                >
                  Abrir Quiosque em Nova Aba
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              {editingLocation ? 'Editar Local' : 'Novo Local'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Nome *</Label>
              <Input
                data-testid="location-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome do estabelecimento"
                className="h-11 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Endereço</Label>
              <Input
                data-testid="location-address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Endereço completo"
                className="h-11 text-base"
              />
            </div>
            
            {/* GPS Coordinates */}
            <div className="space-y-2 pt-2 border-t border-[#E5E7EB]">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Coordenadas GPS (Geolocalização)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#6B7280]">Latitude</Label>
                  <Input
                    data-testid="location-lat-input"
                    type="number"
                    step="0.000001"
                    value={formData.latitude || ''}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="ex: 38.7223"
                    className="h-11 text-base"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280]">Longitude</Label>
                  <Input
                    data-testid="location-lon-input"
                    type="number"
                    step="0.000001"
                    value={formData.longitude || ''}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="ex: -9.1393"
                    className="h-11 text-base"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Raio Permitido (metros)</Label>
                <Input
                  data-testid="location-radius-input"
                  type="number"
                  min="10"
                  max="5000"
                  value={formData.geo_radius || 100}
                  onChange={(e) => setFormData({ ...formData, geo_radius: parseInt(e.target.value) || 100 })}
                  className="h-11 text-base w-32"
                />
              </div>
              <p className="text-xs text-[#6B7280]">Se preenchido, funcionários fora do raio não poderão registrar ponto</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Desconto Almoço (horas)</Label>
              <Input
                data-testid="location-lunch-deduction-input"
                type="number"
                step="0.25"
                min="0"
                max="3"
                value={formData.lunch_deduction}
                onChange={(e) => setFormData({ ...formData, lunch_deduction: parseFloat(e.target.value) || 0 })}
                placeholder="1"
                className="h-11 text-base w-32"
              />
              <p className="text-xs text-[#6B7280]">Descontado automaticamente das horas líquidas</p>
            </div>
            
            {/* Weekly Hours */}
            <div className="space-y-3 pt-2 border-t border-[#E5E7EB]">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Horas Esperadas por Dia</Label>
              <div className="grid grid-cols-7 gap-3">
                {[
                  { key: 'monday', label: 'Seg' },
                  { key: 'tuesday', label: 'Ter' },
                  { key: 'wednesday', label: 'Qua' },
                  { key: 'thursday', label: 'Qui' },
                  { key: 'friday', label: 'Sex' },
                  { key: 'saturday', label: 'Sáb' },
                  { key: 'sunday', label: 'Dom' },
                ].map(day => {
                  const val = formData.weekly_hours?.[day.key] ?? 0;
                  return (
                    <div key={day.key} className="text-center">
                      <label className={`text-xs font-bold block mb-2 ${val > 0 ? 'text-[#111111]' : 'text-[#9CA3AF]'}`}>
                        {day.label}
                      </label>
                      <Input
                        data-testid={`weekly-${day.key}`}
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        className="text-center text-base font-semibold h-11"
                        value={val}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setFormData({
                            ...formData,
                            weekly_hours: { ...formData.weekly_hours, [day.key]: v }
                          });
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[#6B7280]">Default: 8h (Seg-Sex), 0h (Sáb-Dom)</p>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t border-[#E5E7EB]">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="px-6">Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="save-location-btn" className="px-6 bg-[#0029FF] hover:bg-[#0022CC]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Breakdown Dialog */}
      <Dialog open={!!breakdownLoc} onOpenChange={(open) => { if (!open) { setBreakdownLoc(null); setBreakdown(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="breakdown-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Equipa — {breakdownLoc?.name}
            </DialogTitle>
          </DialogHeader>
          {breakdownLoading ? (
            <div className="py-12 text-center">
              <div className="w-6 h-6 border-2 border-[#111111] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : breakdown ? (
            <div className="py-2 space-y-6">
              <div className="text-sm text-[#6B7280]" data-testid="breakdown-total">
                <strong className="text-[#111111] text-base">{breakdown.total_employees}</strong> funcionários ativos
              </div>
              {breakdown.departments.map((dept) => (
                <section key={dept.department_key} className="border border-[#E5E7EB]" data-testid={`dept-${dept.department_key}`}>
                  <div className="bg-[#111111] text-white px-4 py-2 flex items-center justify-between">
                    <h3 className="text-sm uppercase tracking-widest font-bold">{dept.department_label}</h3>
                    <span className="text-xs">{dept.total} {dept.total === 1 ? 'pessoa' : 'pessoas'}</span>
                  </div>
                  {dept.positions.length === 0 ? (
                    <p className="px-4 py-3 text-xs text-[#9CA3AF]">Nenhum funcionário neste setor.</p>
                  ) : (
                    <ul>
                      {dept.positions.map((p) => (
                        <li key={p.position} className="border-b border-[#F3F4F6] last:border-0">
                          <div className="px-4 py-2 bg-[#F9FAFB] flex items-center justify-between">
                            <span className="text-xs uppercase tracking-wider font-bold text-[#0029FF]">
                              {p.position}
                            </span>
                            <span className="text-xs text-[#6B7280]">{p.count}</span>
                          </div>
                          <ul className="divide-y divide-[#F3F4F6]">
                            {p.employees.map((emp) => (
                              <li key={emp.employee_id} className="px-4 py-2 text-sm flex items-center justify-between" data-testid={`breakdown-emp-${emp.employee_id}`}>
                                <span>{emp.name}</span>
                                {emp.contract_type === 'a_termo' && emp.renewal_date && (
                                  <span className="text-[10px] text-[#F59E0B] font-bold">
                                    Renova: {emp.renewal_date}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
              {breakdown.unassigned.length > 0 && (
                <section className="border border-dashed border-[#E5E7EB]" data-testid="dept-unassigned">
                  <div className="bg-[#FEF3C7] text-[#92400E] px-4 py-2">
                    <h3 className="text-sm uppercase tracking-widest font-bold">Sem setor atribuído</h3>
                    <p className="text-[11px] mt-1">Edite estes funcionários para atribuir setor e função.</p>
                  </div>
                  <ul className="divide-y divide-[#F3F4F6]">
                    {breakdown.unassigned.map((emp) => (
                      <li key={emp.employee_id} className="px-4 py-2 text-sm">{emp.name}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBreakdownLoc(null); setBreakdown(null); }}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
